/**
 * SubscriptionService — Abonnements Premium
 * Toutes les clés AsyncStorage préfixées par userId
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { getAuthToken, getUserIdFromToken } from './authHelpers';

const API_URL = 'https://on0ym6si4l.execute-api.eu-west-3.amazonaws.com/prod';

// ─── Clés AsyncStorage par utilisateur ───────────────────────
function KEY_PLAN(uid)    { return `outfit_plan_${uid    || 'guest'}`; }
function KEY_SUB(uid)     { return `outfit_subscription_${uid || 'guest'}`; }
function KEY_EXPIRES(uid) { return `outfit_premium_expires_${uid || 'guest'}`; }

async function getAuthHeader(accessToken) {
  if (accessToken) return { Authorization: `Bearer ${accessToken}` };
  try {
    const token = await getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

// ─── Charger l'abonnement depuis le serveur ──────────────────
export async function fetchSubscription(userId, accessToken) {
  const uid = userId || 'guest';
  const authHeader = await getAuthHeader(accessToken);

  try {
    const res = await fetch(`${API_URL}/subscription`, {
      headers: { 'Content-Type': 'application/json', ...authHeader }
    });

    if (!res.ok) {
      // Retourner le cache si serveur indisponible
      const cached = await AsyncStorage.getItem(KEY_SUB(uid));
      return cached ? JSON.parse(cached) : null;
    }

    const data = await res.json();

    // Cache par userId — tous les setItem doivent recevoir une STRING
    // (sinon crash natif AsyncStorage NSString:initWithContentsOfFile).
    try { await AsyncStorage.setItem(KEY_SUB(uid), JSON.stringify(data || {})); } catch (_) {}
    if (data && data.status === 'active') {
      const period = (typeof data.period === 'string' && data.period) ? data.period : 'monthly';
      try { await AsyncStorage.setItem(KEY_PLAN(uid), period); } catch (_) {}
      if (data.current_period_end != null) {
        try { await AsyncStorage.setItem(KEY_EXPIRES(uid), String(data.current_period_end)); } catch (_) {}
      }
    } else {
      try { await AsyncStorage.setItem(KEY_PLAN(uid), 'free'); } catch (_) {}
    }
    console.log('[SUB] fetched uid:', uid, '| status:', data?.status, '| plan:', data?.period);
    return data;
  } catch(e) {
    console.warn('[SUB] fetchSubscription error:', e.message);
    try {
      const cached = await AsyncStorage.getItem(KEY_SUB(uid));
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  }
}

// ─── Lire le cache local ─────────────────────────────────────
export async function getCachedSubscription(userId) {
  try {
    const cached = await AsyncStorage.getItem(KEY_SUB(userId || 'guest'));
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
}

// ─── Activer un abonnement ───────────────────────────────────
export async function activateSubscription({
  tier, period, platform,
  stripeSubId, stripeCustomerId,
  appleTxnId, appleProductId,
  googleToken, googleProductId,
  priceEur, billingEmail, billingName, billingCountry,
  pushToken,
}) {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_URL}/subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify({
      tier:                   tier || 'premium',
      period,
      platform,
      stripe_subscription_id: stripeSubId,
      stripe_customer_id:     stripeCustomerId,
      apple_original_txn_id:  appleTxnId,
      apple_product_id:       appleProductId,
      google_purchase_token:  googleToken,
      google_product_id:      googleProductId,
      price_eur:              priceEur,
      billing_email:          billingEmail,
      billing_name:           billingName,
      billing_country:        billingCountry,
      device_platform:        Platform.OS,
      app_version:            Constants.expoConfig?.version || '1.0.0',
      push_token:             pushToken,
    }),
  });
  const data = await res.json();

  if (res.ok && data.success) {
    // Récupérer l'userId depuis Cognito pour la clé cache
    let uid = 'guest';
    try {
      uid = (await getUserIdFromToken()) || 'guest';
    } catch {}

    const now = new Date().toISOString();
    const end = period === 'yearly'
      ? new Date(Date.now() + 365*24*60*60*1000).toISOString()
      : new Date(Date.now() + 30*24*60*60*1000).toISOString();

    const subCache = { status: 'active', period, current_period_start: now, current_period_end: end, platform };
    await AsyncStorage.setItem(KEY_PLAN(uid),    period);
    console.log('[SUB] cache saved uid:', uid, 'plan:', period);
    await AsyncStorage.setItem(KEY_SUB(uid),     JSON.stringify(subCache));
    await AsyncStorage.setItem(KEY_EXPIRES(uid), end);
    console.log('[SUB] activated uid:', uid, '| plan:', period);
  }
  return data;
}

// ─── Changer de plan ─────────────────────────────────────────
export async function changePlan(period, stripeSubId = null) {
  const authHeader = await getAuthHeader();
  const res = await fetch(`${API_URL}/subscription`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify({ period, stripe_subscription_id: stripeSubId }),
  });
  const data = await res.json();
  if (res.ok && data.success) {
    let uid = 'guest';
    try {
      uid = (await getUserIdFromToken()) || 'guest';
    } catch {}
    await AsyncStorage.setItem(KEY_PLAN(uid), period);
    await AsyncStorage.removeItem(KEY_SUB(uid));
  }
  return data;
}

// ─── Annuler ─────────────────────────────────────────────────
export async function cancelSubscription(reason = null) {
  // ═══ v1.7.56 : logs détaillés pour diagnostic ═══
  // Avant : si l'API plantait (404, 500, network), on retournait juste
  //   { success: false } sans détail. L'user voyait "impossible de
  //   désactiver" sans qu'on puisse savoir pourquoi.
  // Maintenant : logs précis status/body pour debug + on essaie POST en
  //   fallback (si Lambda pas redéployée avec le DELETE endpoint).
  const authHeader = await getAuthHeader();
  console.log('[Sub] cancel: calling DELETE /subscription');

  let res, data;
  try {
    res = await fetch(`${API_URL}/subscription`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: JSON.stringify({ reason }),
    });
  } catch (netErr) {
    console.warn('[Sub] DELETE network error:', netErr.message);
    return { success: false, error: 'network', message: netErr.message };
  }

  try {
    data = await res.json();
  } catch (parseErr) {
    console.warn(`[Sub] DELETE parse error (status=${res.status}):`, parseErr.message);
    data = {};
  }

  console.log(`[Sub] cancel response status=${res.status} ok=${res.ok} body=${JSON.stringify(data).slice(0, 200)}`);

  // Cas 1 : Succès normal
  if (res.ok && data.success) {
    let uid = 'guest';
    try { uid = (await getUserIdFromToken()) || 'guest'; } catch {}
    await AsyncStorage.setItem(KEY_PLAN(uid), 'cancelled');
    await AsyncStorage.removeItem(KEY_SUB(uid));
    // v1.12.683 — GA4 : résiliation d'abonnement
    try {
      const { trackEvent } = require('./AnalyticsService');
      trackEvent('cancel_subscription', { reason: reason || 'unspecified' });
    } catch {}
    return data;
  }

  // Cas 2 : 404 → distinguer endpoint absent vs profile-not-found
  // - "Profile not found" : la Lambda répond 404 car le profil n'existe pas
  //   en DB (rare, mais peut arriver en sandbox). On le traite comme un
  //   succès idempotent : du point de vue user, il n'y a rien à annuler.
  // - "Not found" sans plus de détail : la route DELETE n'est pas déployée
  //   (vrai cas d'erreur où l'user doit attendre).
  if (res.status === 404) {
    const errMsg = (data?.error || '').toLowerCase();
    if (errMsg.includes('profile not found')) {
      console.warn('[Sub] DELETE: profile not found in DB → idempotent success');
      let uid = 'guest';
      try { uid = (await getUserIdFromToken()) || 'guest'; } catch {}
      await AsyncStorage.setItem(KEY_PLAN(uid), 'cancelled');
      return { success: true, message: 'No subscription found, marked as cancelled locally' };
    }
    console.warn('[Sub] DELETE returned 404 — Lambda probablement pas redéployée');
    return { success: false, error: 'endpoint_not_found',
             message: 'Service temporairement indisponible. Redéploiement Lambda nécessaire.' };
  }

  // Cas 3 : 401/403 → token expiré/manquant
  if (res.status === 401 || res.status === 403) {
    console.warn(`[Sub] DELETE auth error ${res.status}`);
    return { success: false, error: 'unauthorized',
             message: data?.error || 'Authentification expirée' };
  }

  // Cas 4 : 500 → erreur serveur (SQL, etc.)
  if (res.status >= 500) {
    return { success: false, error: 'server_error',
             message: data?.error || `Erreur serveur (${res.status})` };
  }

  // Cas 5 : Pas de subscription active à annuler — on accepte quand même
  // car d'un point de vue user le résultat est le même
  if (data.message && /no active subscription/i.test(data.message)) {
    let uid = 'guest';
    try { uid = (await getUserIdFromToken()) || 'guest'; } catch {}
    await AsyncStorage.setItem(KEY_PLAN(uid), 'cancelled');
    return { success: true, message: data.message };
  }

  return { success: false, error: data?.error || 'unknown',
           message: data?.error || `HTTP ${res.status}` };
}

// ─── Vérifier le plan local ───────────────────────────────────
export async function isPremiumLocal(userId) {
  try {
    const uid     = userId || 'guest';
    const plan    = await AsyncStorage.getItem(KEY_PLAN(uid));
    const expires = await AsyncStorage.getItem(KEY_EXPIRES(uid));
    if (!plan || plan === 'free' || plan === 'cancelled') return false;
    if (expires && new Date(expires) < new Date()) return false;
    return plan === 'monthly' || plan === 'yearly';
  } catch { return false; }
}
